<script>
  import { createEventDispatcher, getContext } from "svelte";

  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import CircleXIcon from "@local/assets/icons/circle-x.svg";

  import { Modal } from "svelte-utilities";
  import LibrarySection from "#lib/sidebar/LibrarySection.svelte";

  import { api } from "#src/api";
  import imageUrl from "#src/extensions/image-url.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";

  export let border = false;
  export let label = "Label";
  export let labelWidth = null;
  export let product;
  export let disabled = false;
  export let readonly = false;
  export let placeholder = "";
  export let outerBorder = true;
  export let orgid;

  const dispatch = createEventDispatcher();
  const categories = getContext("categories");

  let input;
  let focused = false;

  let modal;
  let selected = null;
  let textValue = product.category;
  let categoryName = "";

  $: ordered = orderedList($categories);
  $: lw = labelWidth ? `width:${labelWidth};` : "";
  $: fetchCategory(product);
  $: displayValue =
    product.category_id === "Mixed" ? "Mixed" : product.category_id ? categoryName : textValue;

  async function fetchCategory(product) {
    if (!product.category_id) return;
    if (product.category_id === "Mixed") return;
    const r = await api.from("product_categories").select("*").eq("id", product.category_id).single();

    if (r.data) {
      categoryName = r.data.name;
    }
  }

  function openModal() {
    selected = null;
    modal.open();
  }

  function parseInput(val) {
    textValue = val;
    if (textValue !== product.category) {
      dispatch("input", { prop: "category", value: textValue });
    }
  }

  function focusInput() {
    input.focus();
  }

  function handleFocus(evt) {
    focused = true;
    input.select();
  }

  function handleKeydown(evt) {
    if (evt.key === "Enter") {
      evt.stopPropagation();
      parseInput(evt.target.value);
    }
  }

  function handleBlur(evt) {
    focused = false;
    parseInput(evt.target.value);
  }

  function selectCategory(e) {
    const { record } = e.detail;
    selected = record.id;
  }

  function chooseCategory() {
    dispatch("input", { prop: "category_id", value: selected });
  }

  function deselectCategory() {
    dispatch("input", { prop: "category_id", value: null });
  }

  async function uploadImage(e) {
    if (e.detail.type && e.detail.data) {
      const uuid = crypto.randomUUID();
      const filename = `${uuid}/${e.detail.filename}`;
      const { data: result, error } = await api.storage.from("images").upload(filename, e.detail.data, {
        contentType: e.detail.contentType,
      });

      if (!error) {
        image = {
          id: uuid,
          bucket: "images",
          object_id: result.path,
          content_type: e.detail.contentType,
          filename: e.detail.filename,
        };
      }
    }
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="prop-container"
  class:has-outer-border={outerBorder}
  class:has-inner-border={!outerBorder}
  class:focused
  class:readonly={readonly || disabled}
  class:visible-border={border}
  on:click|stopPropagation={focusInput}>
  <div class="label" style={lw}>
    <slot name="label">
      {label}
    </slot>
  </div>
  <div class="grow">
    {#if readonly || product.category_id}
      <div class="flex items-center justify-start">
        <div bind:this={input} class="mx-2">{displayValue}</div>
        {#if !disabled && product.category_id && product.category_id !== "Mixed"}
          <button class="text-gray-400" on:click|stopPropagation={deselectCategory}>
            <CircleXIcon />
          </button>
        {/if}
      </div>
    {:else}
      <input
        size="1"
        bind:this={input}
        class="input"
        {placeholder}
        {disabled}
        bind:value={textValue}
        on:focus={handleFocus}
        on:keydown={handleKeydown}
        on:blur={handleBlur} />
    {/if}
  </div>
  {#if !disabled}
    <button on:click|stopPropagation={openModal}>
      <EllipsisIcon />
    </button>
  {/if}
</div>

<Modal
  bind:this={modal}
  width="36rem"
  closeable
  fullframe
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Choose Category", type: "confirm", disabled: !selected },
  ]}
  on:confirm={chooseCategory}>
  <div slot="title">Choose Category</div>
  <div slot="content" class="overflow-y-auto">
    <div class="p-4 space-y-2 text-xs">
      <LibrarySection section={{ records: ordered }} {selected} on:select={selectCategory}>
        <div slot="thumbnail" let:record class="w-full h-full">
          {#if record.image}
            <img src={imageUrl(record.image)} alt={record.name} class="w-full h-full object-cover" />
          {/if}
        </div>
        <div slot="caption" let:record>
          {record.name}
        </div>
      </LibrarySection>
    </div>
  </div>
</Modal>

<style lang="scss">
  input {
    border: none;
    background-color: none;
    outline: none;
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .prop-container {
    @apply w-full flex items-center rounded bg-white;

    &.has-outer-border {
      @apply p-2;

      &:hover:not(.readonly):not(.focused),
      &.visible-border {
        @apply ring-1 ring-inset ring-gray-300;
      }

      &.focused {
        @apply ring-2 ring-inset ring-blue-500;
      }
    }

    .label {
      @apply truncate text-gray-400 flex-none;
    }

    .input {
      @apply min-w-0 grow shrink ml-2;
    }

    &.has-inner-border {
      .input {
        @apply p-1 rounded;
      }
    }

    &.has-inner-border:not(.readonly):not(.focused) {
      .input:hover {
        @apply ring-1 ring-inset ring-gray-300;
      }
    }

    &.has-inner-border.focused {
      .input {
        @apply ring-2 ring-inset ring-blue-500;
      }
    }
  }

  .image-container {
    width: 150px;
  }

  .confirm-button {
    @apply flex-none border-l p-4;

    &:hover:not(.disabled) {
      @apply bg-gray-100;
    }
  }
</style>
